import { Autocomplete, FormControl, TextField } from '@mui/material';
import type { GcpSubnet } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';
import type { GcpNetwork } from './gcpNetworkSelect';

interface OwnedSubnetsSelectProps {
  accountId: string;
  subnetName: string;
  region: string;
  network?: Omit<GcpNetwork, 'isPeered'>;
  onChange: (
    subnetName?: string,
    isUserChange?: boolean,
    networkHostProject?: string
  ) => void;
}

export const GcpSubnetSelect = (props: OwnedSubnetsSelectProps) => {
  if (!props.network || !props.region || !props.accountId) {
    return <EmptySelect title='' />;
  }

  return props.network?.isShared ? (
    <SharedSubnetsSelect {...props} />
  ) : (
    <OwnedSubnetsSelect {...props} />
  );
};

const OwnedSubnetsSelect = (props: OwnedSubnetsSelectProps) => {
  const dal = useDAL();
  const { data: body } = dal.restore.gcp.useListSubnets(
    props.accountId,
    props.region,
    props.network?.networkName
  );
  return <InnerGcpSubnetSelect subnets={body?.subnets ?? []} {...props} />;
};

const SharedSubnetsSelect = (props: OwnedSubnetsSelectProps) => {
  const dal = useDAL();
  const { data: body } = dal.restore.gcp.useListSharedSubnets(
    props.accountId,
    props.region,
    props.network?.networkName
  );
  return <InnerGcpSubnetSelect subnets={body?.subnets ?? []} {...props} />;
};

const InnerGcpSubnetSelect = ({
  subnetName,
  network,
  onChange,
  subnets,
}: OwnedSubnetsSelectProps & { subnets: GcpSubnet[] }) => {
  const filteredSubnets = useMemo(() => {
    return subnets
      .filter((subnet) =>
        network?.networkName ? subnet.vpcNetwork === network.networkName : true
      )
      .map((subnet) => ({
        label: subnet.name,
        project: subnet.project,
      }));
  }, [subnets, network]);

  return (
    <FormControl size='small' className='flex-1'>
      <Autocomplete
        sx={{
          '& .MuiInputBase-root': {
            height: 30,
            minHeight: 30,
          },
        }}
        clearOnBlur
        data-testid='gcpSubnetSelectByName'
        value={filteredSubnets.find((s) => s.label === subnetName) || null}
        onChange={(event, newValue) =>
          onChange(newValue?.label || undefined, !!event, newValue?.project)
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.label === value.label}
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={filteredSubnets}
      />
    </FormControl>
  );
};
